import React, { Suspense, useEffect, useState, lazy } from "react";
import { Routes, Route, useLocation } from "react-router-dom";
import retina from "retinajs";
import { AnimatePresence } from "framer-motion";
import GlobalContext from "./Context/Context";
import ScrollToTopButton from "./Components/ScrollToTop";
import "./i18n";

// ---------- Eager imports ----------
import CheckoutList from "./Pages/meldi-dham-pages/product/CheckoutList";
import PaymentPage from "./Pages/meldi-dham-pages/product/PaymentPage";
import OrderSuccess from "./Pages/meldi-dham-pages/product/OrderSuccessList";
import ServicesListPage from "./Pages/meldi-dham-pages/ServicesListPage";
import WhyChoosePage from "./Pages/meldi-dham-pages/WhyChoosePage";
import CategoryProductList from "./Pages/meldi-dham-pages/product-category/CategoryProductList";
import OurServicePage from "./Pages/meldi-dham-pages/OurServicePAge";

// ---------- Lazy imports ----------
const HomePage = lazy(() => import("./Pages/meldi-dham-pages/HomePage"));
const GalleryPage = lazy(
  () => import("./Pages/meldi-dham-pages/GalleryAlbumPage"),
);
const MeldiDhamEvents = lazy(
  () => import("./Pages/meldi-dham-pages/MeldiDhamEvents"),
);
const MeldiDhamAboutUsPage = lazy(
  () => import("./Pages/meldi-dham-pages/MeldiDhamAboutUsPage"),
);
const MeldiDhamFQPage = lazy(
  () => import("./Pages/meldi-dham-pages/MeldiDhamF&QPage"),
);
// const CertificatesPage = lazy(
//   () => import("./Pages/meldi-dham-pages/CertificatesPage"),
// );
const CertificatesPage = lazy(
  () => import("./Pages/meldi-dham-pages/CertificatesPage"),
);
const MeldiDhamBlogPage = lazy(
  () => import("./Pages/meldi-dham-pages/MeldiDhamBlogPage"),
);
const PhotoAndVideoListPage = lazy(
  () => import("./Pages/meldi-dham-pages/PhotoAndVideoListPage"),
);
const BlogDetailPage = lazy(
  () => import("./Pages/meldi-dham-pages/BlogDetailPage"),
);
const CategoryListPage = lazy(
  () => import("./Pages/meldi-dham-pages/product-category/CategoryList"),
);
const ProductDetails = lazy(
  () => import("./Pages/meldi-dham-pages/product/ProducDetails"),
);

const OurEvents = lazy(() => import("./Pages/ecd-pages/OurEvents"));
const EcdAboutUsPage = lazy(() => import("./Pages/ecd-pages/EcdAboutUsPage"));

const Footer = lazy(() => import("./Pages/Footer"));
const FooterStyle01Page = lazy(
  () => import("./Pages/Footer/FooterStyle01Page"),
);
const FooterStyle02Page = lazy(
  () => import("./Pages/Footer/FooterStyle02Page"),
);
const ContactUsSimplePage = lazy(
  () => import("./Pages/Contact/ContactUsSimplePage"),
);
const NotFoundPage = lazy(() => import("./Pages/404"));
const OurServicesPage = lazy(() => import("./Pages/Services/OurServicesPage"));

function App() {
  const location = useLocation();

  const [headerHeight, setHeaderHeight] = useState(147); // ✅ default fallback 80
  const [footerHeight, setFooterHeight] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [customModal, setCustomModal] = useState({ el: null, isOpen: false });

  // ---------- Measure header safely ----------
  useEffect(() => {
    const updateHeaderHeight = () => {
      const header = document.querySelector("header");
      if (header) {
        setHeaderHeight(header.offsetHeight || 147); //80
      }
    };

    updateHeaderHeight();
    window.addEventListener("resize", updateHeaderHeight);

    return () => window.removeEventListener("resize", updateHeaderHeight);
  }, []);

  // ---------- Retina ----------
  useEffect(() => {
    window.addEventListener("load", retina(document.querySelectorAll("img")));
  }, []);

  // ---------- Utilities on route change ----------
  useEffect(() => {
    setTimeout(() => {
      import("./Functions/Utilities").then((module) => {
        module.SetHeaderMenuPos();
        module.setDocumentFullHeight();
      });
    }, 300);
  }, [location]);

  // ---------- Modal body lock ----------
  useEffect(() => {
    document.body.classList.toggle("overflow-hidden", isModalOpen);
  }, [isModalOpen]);

  // ---------- Scroll reset ----------
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
    setFooterHeight(0);
    setCustomModal({ el: null, isOpen: false });
  }, [location]);

  return (
    <GlobalContext.Provider
      value={{
        headerHeight,
        setHeaderHeight,
        footerHeight,
        setFooterHeight,
        isModalOpen,
        setIsModalOpen,
        customModal,
        setCustomModal,
      }}
    >
      <div className="App" style={{ "--header-height": `${headerHeight}px` }}>
        <main
          style={{
            marginTop:
              window.innerWidth <= 576
                ? 70 // small mobile
                : window.innerWidth <= 768
                  ? 80 // mobile
                  : window.innerWidth <= 992
                    ? 120 // tablet
                    : 147,
            marginBottom: footerHeight,
          }}
        >
          {/* <main style={{ marginTop: headerHeight, marginBottom: footerHeight }}> */}
          <ScrollToTopButton />

          <AnimatePresence mode="wait">
            <Suspense fallback={<div style={{ minHeight: "100vh" }} />}>
              <Routes location={location} key={location.pathname}>
                <Route path="/" element={<HomePage />} />
                <Route path="/page/about" element={<EcdAboutUsPage />} />
                <Route
                  path="/page/about-us"
                  element={<MeldiDhamAboutUsPage />}
                />
                <Route path="/page/gallery&album" element={<GalleryPage />} />
                <Route
                  path="/page/gallery&album/:id"
                  element={<PhotoAndVideoListPage />}
                />
                <Route
                  path="/page/meldidham-events"
                  element={<MeldiDhamEvents />}
                />
                <Route path="/page/f&q" element={<MeldiDhamFQPage />} />
                <Route path="/page/certificates" element={<CertificatesPage />} />
                <Route path="/page/blog" element={<MeldiDhamBlogPage />} />
                <Route path="/page/blog/:id" element={<BlogDetailPage />} />
                <Route path="/page/categories" element={<CategoryListPage />} />
                <Route path="/category/:id" element={<CategoryProductList />} />
                <Route path="/product/:id" element={<ProductDetails />} />
                <Route path="/checkout" element={<CheckoutList />} />
                <Route path="/payment/:id" element={<PaymentPage />} />
                <Route path="/order-success" element={<OrderSuccess />} />
                <Route path="/page/our-services" element={<OurServicePage />} />
                <Route
                  path="/page/service/:id"
                  element={<ServicesListPage />}
                />
                <Route
                  path="/page/contact-simple"
                  element={<ContactUsSimplePage />}
                />
                <Route path="*" element={<NotFoundPage />} />
                <Route path="/why-choose" element={<WhyChoosePage />} />
              </Routes>
            </Suspense>
          </AnimatePresence>
        </main>
      </div>
    </GlobalContext.Provider>
  );
}

export default App;
